import { getUrlTitle, toURL } from "@prismicio/editor-support/URL"
import {
  Box,
  ConjoinedIcons,
  Dialog,
  DialogContent,
  FormInput,
  SelectButton,
  Text
} from "@prismicio/editor-ui"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import type { Editor } from "@tiptap/core"
import { type KeyboardEvent, useEffect, useState } from "react"

import { createRunCommandsFn } from "../../models/MenuItems"

interface SetWebLinkModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  editor: Editor
}

export function SetWebLinkModal(props: SetWebLinkModalProps) {
  const { open } = props
  const {
    url,
    setUrl,
    onOpenChange,
    onKeyDown,
    openInNewTab,
    setOpenInNewTab,
    onSetUrl,
    selectionUrl,
    selectionUrlTitle
  } = useLinkModal(props)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      hasClose={false}
      size="small"
    >
      <DialogContent>
        <Box flexDirection="column">
          <Box flexDirection="column" height="100%" padding={16} gap={4}>
            <FormInput
              value={url}
              onValueChange={setUrl}
              placeholder="Paste URL"
              icon="public"
              onKeyDown={onKeyDown}
            />
            {selectionUrl && (
              <Box flexDirection="column" padding={{ top: 8 }} gap={4}>
                <Text variant="smallBold" color="grey11">
                  Linked to
                </Text>
                <Box
                  backgroundColor="grey4"
                  borderRadius={6}
                  padding={8}
                  alignItems="center"
                  gap={8}
                >
                  <ConjoinedIcons largeIconName="public" smallIconName="link" />
                  <Box flexDirection="column">
                    <Text variant="bold" color="grey12">
                      {selectionUrlTitle}
                    </Text>
                    <Text color="grey11" variant="small">
                      Web page
                    </Text>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          {url && (
            <Box
              flexDirection="column"
              padding={{ block: 8 }}
              border={{ top: true }}
            >
              <SelectButton
                onSelect={() => setOpenInNewTab(!openInNewTab)}
                icon={openInNewTab ? "checkBox" : "checkBoxOutlinedBlank"}
                textContent="Open in new tab"
              />
              <SelectButton
                onSelect={() => onSetUrl(url)}
                icon="link"
                textContent="Link to URL"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

function useLinkModal(props: SetWebLinkModalProps) {
  const { editor, setOpen } = props

  const [url, setUrl] = useState("")
  const [openInNewTab, setOpenInNewTab] = useState(false)

  const selectionUrl = getSelectionUrl(editor)
  const selectionOpenInNewTab = getIsOpenInNewTab(editor)

  useEffect(() => {
    setUrl(selectionUrl ?? "")
  }, [selectionUrl])

  useEffect(() => {
    setOpenInNewTab(selectionOpenInNewTab ?? false)
  }, [selectionOpenInNewTab])

  const formattedSelectionUrl = selectionUrl ? toURL(selectionUrl) : undefined
  const selectionUrlTitle = formattedSelectionUrl
    ? getUrlTitle(formattedSelectionUrl)
    : ""

  function onSetUrl(url: string) {
    const target = openInNewTab ? "_blank" : "_self"

    const setLink = createRunCommandsFn((commands) =>
      commands
        .extendMarkRange(RichTextNodeType.hyperlink)
        .setWebLink({ href: url, target })
    )
    setLink(editor)
    setOpen(false)
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") onSetUrl(url)
  }

  function onOpenChange(open: boolean) {
    setUrl("")
    setOpenInNewTab(false)
    setOpen(open)
  }

  return {
    url,
    setUrl,
    onSetUrl,
    selectionUrl,
    selectionUrlTitle,
    openInNewTab,
    selectionOpenInNewTab,
    setOpenInNewTab,
    onKeyDown,
    onOpenChange
  }
}

function getSelectionUrl(editor: Editor) {
  const linkAttributes = editor.getAttributes(RichTextNodeType.hyperlink)

  const url = linkAttributes["href"]
    ? (linkAttributes["href"] as string)
    : undefined

  return url
}

function getIsOpenInNewTab(editor: Editor) {
  const linkAttributes = editor.getAttributes(RichTextNodeType.hyperlink)
  return linkAttributes["target"] === "_blank"
}
